import React, { useState } from "react";

import * as R from "ramda";

import styled from "react-emotion";

import FaCommentingO from "react-icons/lib/fa/commenting-o";
import FaFacebook from "react-icons/lib/fa/facebook";
import FaLinkedIn from "react-icons/lib/fa/linkedin";
import FaApple from "react-icons/lib/fa/apple";
import FaInstagram from "react-icons/lib/fa/instagram";
import NewTwitter from "../../icons/Twitter";

import {
  Box,
  Flex,
  Image,
  ItemGroup,
  Link,
  Markup,
  Text,
} from "src/components";
import { shortenLocaleName } from "src/utils";

import ContactForm from "./ContactForm";
import GooglePlayIcon from "./GooglePlayIcon";

const locales = [
  {
    key: "en",
    label: "In English",
    isDefault: true,
    root: "/",
  },
  {
    key: "es",
    label: "En español",
    root: "/es",
  },
];

const LocaleSelector = ({ locale, path }) => {
  const [[currentLocale], [newLocale]] = R.partition(
    R.propEq("key", locale),
    locales
  );

  const alternatePath = newLocale.isDefault
    ? path.slice(currentLocale.key.length + 1)
    : `/${newLocale.key}${path}`;

  return (
    <Link to={alternatePath} color="white" fontSize={2}>
      {newLocale.label}
    </Link>
  );
};

const getFooterLinks = locale =>
  R.pipe(
    R.path(["footerLinks", "edges"]),
    R.map(R.prop("node")),
    R.find(R.pipe(R.prop("node_locale"), shortenLocaleName, R.equals(locale)))
  );

const Disclaimers = ({
  data,
  isInvestments,
  includeNcuaLogo,
  includeEhlLogo,
  t,
}) => {
  if (includeNcuaLogo || includeEhlLogo) {
    return (
      <Box>
        {isInvestments ? (
          <Box mb={2}>
            <Markup
              my={[2, undefined, 0]}
              html={data.investmentsDisclaimer.content.childMarkdownRemark.html}
              linkColor="white"
              fontSize={2}
            />
          </Box>
        ) : (
          <Flex
            align="center"
            mb={2}
            flexDirection={["column", undefined, "row"]}
            printStyles={{ display: "block" }}
          >
            {includeEhlLogo ? (
              <Box printStyles={{ display: "none" }}>
                <Image
                  resolutions={data.ehlLogo.resolutions}
                  mr={[0, undefined, 3]}
                  mb={[2, undefined, 0]}
                  alt="Equal Housing Lender"
                />
              </Box>
            ) : null}
            {includeNcuaLogo ? (
              <Flex
                flexDirection={["column", undefined, "row"]}
                align={["center", undefined, "flex-start"]}
                printStyles={{ display: "block" }}
              >
                <Box printStyles={{ display: "none" }}>
                  <Image
                    resolutions={data.ncuaLogo.resolutions}
                    alt="National Credit Union Administration Logo"
                  />
                </Box>
                <Text
                  ml={[0, undefined, 3]}
                  my={[2, undefined, 0]}
                  fontSize={2}
                  style={{ maxWidth: 350 }}
                  printStyles={{ maxWidth: "100% !important" }}
                >
                  {t("disclaimer.ncua")}
                </Text>
              </Flex>
            ) : null}
          </Flex>
        )}
      </Box>
    );
  }

  return null;
};

const ContactFormMarkup = styled(Markup)`
  h3 {
    margin-bottom: 0;
    font-size: 22px;
  }
`;

const Footer = ({
  data,
  locale,
  location,
  t,
  includeNcuaLogo = true,
  includeEhlLogo = true,
  isInvestments = false,
}) => {
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);

  const connectIcons = [
    {
      icon: FaFacebook,
      title: t("footer.connect.facebookTitle", {
        defaultValue: "Connect with us on Facebook",
      }),
      link: t("footer.connect.facebookLink", {
        defaultValue: "https://www.facebook.com/FinancialCenterFCU",
      }),
    },
    {
      icon: () => NewTwitter({ height: "20", width: "20" }),
      title: t("footer.connect.twitterTitle", {
        defaultValue: "Follow us on Twitter",
      }),
      link: t("footer.connect.twitterLink", {
        defaultValue: "https://twitter.com/FCFCU",
      }),
    },
    {
      icon: FaLinkedIn,
      title: t("footer.connect.linkedInTitle", {
        defaultValue: "Follow us on LinkedIn",
      }),
      link: t("footer.connect.linkedInLink", {
        defaultValue: "https://www.linkedin.com/company/financial-center/",
      }),
    },
    {
      icon: FaInstagram,
      title: t("footer.connect.instagramTitle", {
        defaultValue: "Follow us on Instagram",
      }),
      link: t("footer.connect.instagramLink", {
        defaultValue: "https://www.instagram.com/Financialcenterfcu/",
      }),
    },
    {
      icon: FaApple,
      title: t("footer.connect.appleTitle", {
        defaultValue: "Download our Apple Mobile App",
      }),
      link: t("footer.connect.appleLink", {
        defaultValue:
          "https://itunes.apple.com/us/app/financial-center-mobile-mpower/id594824876?mt=8",
      }),
    },
    {
      icon: GooglePlayIcon,
      title: t("footer.connect.googleTitle", {
        defaultValue: "Download our Google Mobile App",
      }),
      link: t("footer.connect.googleLink", {
        defaultValue: "https://play.google.com/store/apps/details?id=com.fcfcu",
      }),
    },
  ];

  return data ? (
    <Box>
      <Flex
        bg="midGray"
        p={2}
        align="center"
        justify="center"
        color="white"
        flexDirection={["column", "row"]}
      >
        <Text
          fontSize={[4, 5]}
          mr={[0, 3]}
          mb={[2, 0]}
          fontWeight="bold"
          style={{ lineHeight: 1 }}
        >
          {t("footer.connect.title", {
            defaultValue: "Connect with us",
          })}
        </Text>
        <Flex align="center" justify="center">
          {connectIcons.map((connectIcon, index) => {
            const IconComponent = connectIcon.icon;

            return (
              <Box mx={1}>
                <Link
                  color="white"
                  href={connectIcon.link}
                  title={connectIcon.title}
                  target="_blank"
                >
                  <Flex justify="end" align="end" px={1}>
                    <IconComponent size={22} />
                  </Flex>
                </Link>
              </Box>
            );
          })}
        </Flex>
      </Flex>
      <Box
        bg="darkGray"
        color="white"
        pb={4}
        pt={3}
        px={[2, 3, 4, 6]}
        printStyles={{
          backgroundColor: "transparent",
          color: "#000",
          padding: "10px",
        }}
      >
        <Box>
          <Flex flexDirection={["column", "column", "column", "row"]}>
            <Box flex={[undefined, undefined, undefined, 1]}>
              {data && data.whiteLogo && data.whiteLogoBallState ? (
                <Box printStyles={{ display: "none" }}>
                  <Flex mb={2}>
                    <Image
                      resolutions={data.whiteLogo.resolutions}
                      mr={2}
                      alt="Financial Center First logo"
                    />
                    <Image
                      resolutions={data.whiteLogoBallState.resolutions}
                      alt="Ball State Financial logo"
                    />
                  </Flex>
                </Box>
              ) : null}
              <Text mt={0} fontSize={2}>
                {t("footer.addressLine")}
                <br />
                {t("footer.addressCityState")}
              </Text>
              <Text fontSize={2} mt={2}>
                {t("footer.phone")}
              </Text>
              <Text fontSize={2} mt={2}>
                {t("footer.routingNumberLabel")}
                {t("footer.routingNumber")}
              </Text>
              <Box printStyles={{ display: "none" }}>
                <Text mt={2}>
                  <LocaleSelector locale={locale} path={location.pathname} />
                </Text>
              </Box>
            </Box>
            <Box
              flex={[undefined, undefined, undefined, 2]}
              px={[0, undefined, undefined, 4]}
              mt={[2, undefined, undefined, 0]}
            >
              <ContactForm
                formKey="footer-contact-form"
                submitted={contactFormSubmitted}
                onSubmitComplete={() => setContactFormSubmitted(true)}
              />
              <ContactFormMarkup
                html={
                  contactFormSubmitted
                    ? data.footerConnectContentSubmitted.content
                        .childMarkdownRemark.html
                    : data.footerConnectContent.content.childMarkdownRemark.html
                }
                linkColor="white"
                fontSize={2}
              />
            </Box>
            <Box
              flex={[undefined, undefined, undefined, 2]}
              mt={[3, 3, 3, 2]}
              printStyles={{ display: "none" }}
            >
              <ItemGroup
                {...getFooterLinks(locale)(data)}
                parentTheme="white"
                padded={false}
                titleProps={{
                  color: "white",
                  mb: -1,
                }}
                containerProps={{
                  px: 0,
                  mr: 2,
                  py: 0,
                  mb: 2,
                }}
                itemProps={{
                  textTransform: "none",
                  fontWeight: "light",
                  fontSize: 2,
                  mt: 0,
                  mb: 1,
                  textDecoration: "underline",
                  activeStyle: {},
                }}
              />
            </Box>
          </Flex>

          <Box mt={3} printStyles={{ marginTop: 16 }}>
            <Disclaimers
              data={data}
              t={t}
              isInvestments={isInvestments}
              includeNcuaLogo={includeNcuaLogo}
              includeEhlLogo={includeEhlLogo}
            />

            <Text fontSize={2}>
              {t("footer.copyrightWithYear", {
                year: new Date().getFullYear(),
              })}
            </Text>
          </Box>
          <Box printStyles={{ display: "none" }}>
            <Text
              mt={2}
              pt={2}
              fontSize={2}
              borderColor="white"
              borderWidth={1}
              borderTop={true}
            >
              {t("footer.disclaimer.external")}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  ) : null;
};

export default Footer;

export const FooterQuery = graphql`
  fragment FooterQuery on RootQueryType {
    ncuaLogo: imageSharp(id: { regex: "/ncua-logo-white.png/" }) {
      resolutions(width: 150, height: 63) {
        ...GatsbyImageSharpResolutions_withWebp_noBase64
      }
    }
    ehlLogo: imageSharp(id: { regex: "/ehl-logo.png/" }) {
      resolutions(width: 63, height: 63) {
        ...GatsbyImageSharpResolutions_withWebp_noBase64
      }
    }
    investmentsDisclaimer: contentfulBody(
      node_locale: { eq: $locale }
      key: { eq: "investments-disclaimer" }
    ) {
      ...Body
    }
    footerConnectContent: contentfulBody(
      node_locale: { eq: $locale }
      key: { eq: "footer-email-signup" }
    ) {
      ...Body
    }
    footerConnectContentSubmitted: contentfulBody(
      node_locale: { eq: $locale }
      key: { eq: "footer-email-signup-submitted" }
    ) {
      ...Body
    }
    footerLinks: allContentfulItemGroup(
      filter: { key: { eq: "footer-links" } }
    ) {
      edges {
        node {
          ...ItemGroupContents
          node_locale
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/Layout/Footer.js